/**
 * Performance Monitor Module
 * 
 * Un modulo avanzato per il monitoraggio delle performance dell'applicazione
 * che evita log duplicati e fornisce dati più significativi.
 */

class PerformanceMonitor {
  constructor() {
    this.isEnabled = process.env.REACT_APP_TYPE === 'DEV';
    this.measurements = {};
    this.routeTimings = {};
    this.componentRenders = {};
    this.loggedEntries = new Set();

    // Inizializza se è abilitato
    if (this.isEnabled) {
      this.initObserver();
    }
  }

  /**
   * Inizializza l'observer per le metriche di performance
   */
  initObserver() {
    if (window.PerformanceObserver) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        for (const entry of entries) {
          if (entry.entryType === 'longtask') {
            const now = Date.now();
            const key = `longtask-${Math.floor(entry.duration)}`;

            if (!this.loggedEntries.has(key)) {
              console.warn(`%cLong Task: ${entry.duration.toFixed(2)}ms`, 'color: #dc3545; font-weight: bold');
              this.loggedEntries.add(key);

              // Pulisci dopo 2 secondi
              setTimeout(() => {
                this.loggedEntries.delete(key);
              }, 2000);
            }
          }
        }
      });

      observer.observe({ entryTypes: ['longtask'] });
    }
  }

  /**
   * Segna l'inizio di una misurazione
   * @param {string} id - ID univoco per la misurazione
   * @param {string} category - Categoria della misurazione (route, component, api, etc.)
   */
  start(id, category) {
    if (!this.isEnabled) return;

    const key = `${category}-${id}`;
    this.measurements[key] = {
      startTime: performance.now(),
      category
    };
  }

  /**
   * Segna la fine di una misurazione e calcola la durata
   * @param {string} id - ID univoco per la misurazione
   * @param {string} category - Categoria della misurazione
   * @returns {number|null} La durata in ms, o null se la misurazione non è stata trovata
   */
  end(id, category) {
    if (!this.isEnabled) return null;

    const key = `${category}-${id}`;
    const measurement = this.measurements[key];

    if (measurement) {
      const duration = performance.now() - measurement.startTime;

      // Log solo una volta ogni 2 secondi per ogni categoria/id
      const logKey = `${key}-${Math.floor(Date.now() / 2000)}`;

      if (!this.loggedEntries.has(logKey)) {
        const color = this.getCategoryColor(category);
        console.log(`%c${category}: ${id} - ${duration.toFixed(2)}ms`, `color: ${color}`);

        this.loggedEntries.add(logKey);
        setTimeout(() => this.loggedEntries.delete(logKey), 2000);
      }

      delete this.measurements[key];
      return duration;
    }

    return null;
  }

  /**
   * Rileva il colore da usare per la categoria nei log
   */
  getCategoryColor(category) {
    const colors = {
      route: '#28a745',   // verde
      component: '#007bff', // blu
      api: '#fd7e14',    // arancione
      render: '#6f42c1',  // viola
      default: '#6c757d'  // grigio
    };

    return colors[category] || colors.default;
  }

  /**
   * Traccia il tempo di rendering di un componente
   * @param {string} componentName - Nome del componente
   */
  trackRender(componentName) {
    if (!this.isEnabled) return;

    if (!this.componentRenders[componentName]) {
      this.componentRenders[componentName] = {
        count: 0,
        lastRender: Date.now()
      };
    }

    const component = this.componentRenders[componentName];
    const now = Date.now();
    component.count++;

    // Se sono passati più di 2 secondi dall'ultimo log
    if (now - component.lastRender > 2000 && component.count > 5) {
      console.log(`%cComponent ${componentName} re-rendered ${component.count} times in 2s`, 'color: #6f42c1');
      component.count = 0;
    }

    component.lastRender = now;
  }
}

// Esporta una singola istanza per l'intera applicazione
export default new PerformanceMonitor();
